import React, { useEffect, useState, useRef } from 'react';
import Sortable from 'sortablejs';
import Dialog from '../../GenericComponents/dialog';
import { useFetchDashBoardVUsMutation, useReorderDashBoardVUsMutation } from '../../Redux/Workspace/workspaceActionsSlice';
import { FoundationsButton } from '../../GenericComponents/button';
import { FoundationsGenericToaster } from '../../GenericComponents/FoundationsGenericToaster';
import HandleAnyPopupMessage from '../../Components/handleAnyPopupMessage';

interface ReorderReportsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  WorkspaceId: number;
  onRefresh: () => void;
  reportsData: any;
}
interface VisualUnit {
  VisualUnitId: number;
  Name: string;
  Description: string;
  ViewerType: string;
  XAxisList: [];
  YAxisList: [];
}
const ReorderReportsDialog: React.FC<ReorderReportsDialogProps> = ({ isOpen, onClose, WorkspaceId, onRefresh, reportsData }) => {
  const [fetchDashBoardVUs] = useFetchDashBoardVUsMutation();
  const [summaryVUs, setSummaryVUs] = useState<VisualUnit[]>([]);
  const [reportVUs, setReportVUs] = useState<VisualUnit[]>([]);
  const [isreorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErr, setShowErr] = useState(false);

  const [loading, setLoading] = useState(false);
  const [reorderDashBoardVUs] = useReorderDashBoardVUsMutation();

  const summaryListRef = useRef<HTMLUListElement | null>(null);
  const reportListRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      handleFetchDashBoardVUs();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isreorderDialogOpen) {
      initializeDragAndDrop();
    }
  }, [isreorderDialogOpen, summaryVUs, reportVUs]);


  const handleFetchDashBoardVUs = () => {
    try {
      if (!reportsData || reportsData.length === 0) {
        setError('Sorry, there are no reports available in this workspace.');
        setShowErr(true);
        return;
      }

      const summaryVUs = reportsData.filter((vunit: VisualUnit) => vunit.XAxisList.length <= 0 && vunit.YAxisList.length <= 0 );
      const reportVUs = reportsData.filter((vunit: VisualUnit) => vunit.XAxisList.length > 0 || vunit.YAxisList.length > 0);

      setSummaryVUs(summaryVUs);
      setReportVUs(reportVUs);

      if (summaryVUs.length > 0 || reportVUs.length > 0) {
        setReorderDialogOpen(true);
      } else {
        setError('Sorry, there are no reports available in this workspace.');
        setShowErr(true);
      }
    } catch (err) {
      setError('An error occurred while processing visual units.');
    } finally {
      setLoading(false);
    }
  };
  

  
  const initializeDragAndDrop = () => {
    if (summaryListRef.current) {
      Sortable.create(summaryListRef.current, { animation: 150 });
    }
    if (reportListRef.current) {
      Sortable.create(reportListRef.current, { animation: 150 });
    }
  };

  const extractIdsFromHtml = (selector: string): number[] => {
    const container = document.querySelector(selector);
    if (!container) return [];
    return Array.from(container.children).map((child) => {
      const match = child.id.match(/vu_reorder_(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    });
  };

  const handleSaveChanges = () => {
    try {
      const summaryNormalVUIds = extractIdsFromHtml('#vu_summary_reorder_list');
      const reportNormalVUIds = extractIdsFromHtml('#vu_report_reorder_list');
      const formData = new FormData();

      formData.append('dashBoardId', WorkspaceId.toString());
      summaryNormalVUIds.forEach((id) => formData.append('summaryNormalVUIds', id.toString()));
      reportNormalVUIds.forEach((id) => formData.append('reportNormalVUIds', id.toString()));

      reorderDashBoardVUs(formData)
        .unwrap()
        .then((response) => {
          if (response.statusCode === 201) {
            FoundationsGenericToaster('success', 'Reports reordered successfully.');
            setReorderDialogOpen(false);
            onClose();
            onRefresh();
          } else {
            FoundationsGenericToaster('error', 'Sorry, reports reordered failed. Please try again.');
            setError('Failed to save changes.');
            setReorderDialogOpen(false);
            onClose();

          }
        });
    } catch (err) {
      setError('An error occurred while saving changes.');
    } finally {
      setLoading(false);
    }
  };

  // rendering list of visual unitss dynamically,  generated based on the state.
  const renderVUList = (vus: VisualUnit[], listId: string) => (
    <ul id={listId} ref={listId === 'vu_summary_reorder_list' ? summaryListRef : reportListRef} className="vu-reorder-list ui-sortable">
      {vus.length > 0 ? (
        vus.map((vunit) => (
          <li key={vunit.VisualUnitId} id={`vu_reorder_${vunit.VisualUnitId}`}>
            <div className="vu-reorder-item">
              <img src="../images/drag-handle.png?v=2014-1" alt="Move" className="vu-reorder-drag-img" />
              <div className="vu-reorder-name">{vunit.Name}</div>
              <div className="vu-reorder-desc">{vunit.Description}</div>
            </div>
          </li>
        ))
      ) : (
        <div className="padding-top-large">
          {listId === 'vu_summary_reorder_list' ? 'No summaries are present in this workspace.' : 'No reports are present in this workspace.'}
        </div>
      )}
    </ul>
  );

  return showErr ? (
    <HandleAnyPopupMessage
    isOpen={showErr}
    onClose={() => setShowErr(false)}
    message = {error}
    title="Reorder Reports"
    Link={null}
    linkText=''
    alignLeft={true}
    />
  ) : (
    isOpen &&
    isreorderDialogOpen && (
    <Dialog 
    isOpen={isreorderDialogOpen} 
    onClose={onClose} 
    title="Reorder Reports" 
    width="30%" 
    className="left-align-text"
    buttons={[
        
        {
            id:"cancel-reorder-reports",
            label: 'Cancel',
            onClick: () => {
                setReorderDialogOpen(false);
                onClose();
            },
            className: 'ui-button ui-corner-all ui-widget app-button negative',
        },
        {
            id:"save-reorder-reports",
            label: 'Save Changes',
            onClick: () => handleSaveChanges(),
            className: 'ui-button ui-corner-all ui-widget app-button',
        }
    ]}>
        <>
          <h3>Summaries</h3>
          {renderVUList(summaryVUs, 'vu_summary_reorder_list')}
          <h3>Reports</h3>
          {renderVUList(reportVUs, 'vu_report_reorder_list')}
        
        </>
    </Dialog>
  ) || null
  );
  
};

export default ReorderReportsDialog;
